import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Stars from './Stars';
import * as api from '../lib/api';

const UNLOCK_KEY = 'sirati_site_gate_unlocked';
const WAITLIST_URL = 'https://sirati.kit.com/0fe20f8c46';

// Oct 5, 2026, 8:00 AM Eastern. Hardcoded as a UTC instant (EDT is UTC-4 on
// that date — DST doesn't end until Nov 1, 2026) so it's correct for every
// visitor regardless of their own browser timezone.
const LAUNCH_DATE = new Date('2026-10-05T12:00:00Z');

const getTimeLeft = () => {
  const diff = Math.max(0, LAUNCH_DATE.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
};

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Mins', value: timeLeft.minutes },
    { label: 'Secs', value: timeLeft.seconds },
  ];

  return (
    <div className="w-full text-center" data-testid="site-gate-countdown">
      <p className="font-heading text-2xl sm:text-3xl font-bold text-text-main mb-4">Sirati launches in</p>
      <div className="flex justify-center gap-3">
        {units.map((u) => (
          <div
            key={u.label}
            className="rounded-2xl px-4 py-4 min-w-[92px] backdrop-blur-md shadow-lg"
            style={{ background: 'linear-gradient(135deg, #A99CC8 0%, #4AADA8 100%)' }}
          >
            <p className="text-4xl font-bold text-white tabular-nums">{String(u.value).padStart(2, '0')}</p>
            <p className="text-xs uppercase tracking-wide text-white/80 mt-1">{u.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Wraps the pre-launch entry points (homepage intro + login/signup) during
 * the soft-launch window. Fully inert once SITE_GATE_ENABLED is off — no
 * code change needed to open up on launch day, just flip that Railway
 * env var.
 */
const SiteGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | locked | unlocked
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getSiteGateStatus()
      .then((res) => {
        if (!res.data?.enabled) { setStatus('unlocked'); return; }
        if (localStorage.getItem(UNLOCK_KEY) === 'true') { setStatus('unlocked'); return; }
        setStatus('locked');
      })
      .catch(() => setStatus('unlocked')); // fail open — a status-check hiccup shouldn't lock everyone out
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.unlockSiteGate(password);
      localStorage.setItem(UNLOCK_KEY, 'true');
      setStatus('unlocked');
    } catch {
      setError('Incorrect password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') return null;

  if (status === 'locked') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden feed-ombre-bg">
        <Stars />
        <div className="relative z-10 w-full max-w-sm flex flex-col items-center gap-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-arch-xl p-8 w-full"
            data-testid="site-gate-form"
          >
            <h1 className="font-heading text-2xl font-bold text-text-main mb-2 text-center">Sirati</h1>
            <p className="text-sm text-text-muted mb-6 text-center">
              We're getting ready to launch. Enter the access password to continue.
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Access password"
                autoFocus
                className="w-full px-4 py-2.5 rounded-arch border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800 mb-3"
                data-testid="site-gate-password-input"
              />
              {error && <p className="text-xs text-red-500 mb-3" role="alert">{error}</p>}
              <button
                type="submit"
                disabled={submitting || !password}
                className="w-full py-2.5 rounded-arch bg-primary text-white font-medium disabled:opacity-50 min-h-[44px]"
                data-testid="site-gate-submit-btn"
              >
                {submitting ? 'Checking…' : 'Enter'}
              </button>
            </form>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            href={WAITLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-hover w-full text-center py-3.5 rounded-full font-semibold text-white min-h-[44px] flex items-center justify-center gap-2 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #3A9A95 0%, #8A7BB3 100%)' }}
            data-testid="site-gate-waitlist-btn"
          >
            Join the Waitlist
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </motion.a>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-full"
          >
            <CountdownTimer />
          </motion.div>
        </div>
      </div>
    );
  }

  return children;
};

export default SiteGate;
